import { useTranslation } from "react-i18next"
import { Group, Stack, Switch } from "@mantine/core"

import { useObjectTypeAndIdFromUrl } from "./use-object-type-and-id-from-url"
import { InfiniteUserSelect } from "@components/InfiniteUserSelect"
import { InfiniteScrollingTable } from "@/routes/-components/custom-tanstack-table"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import {
  getFilteredRemindersPaged,
  getGetFilteredRemindersPagedQueryKey,
  ReminderDto,
  useGetAuthenticatedUserSuspense,
} from "@/orval/gen"
import { PersonNameWithUserNameFallback } from "@/routes/-components/chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { IconCircleCheck, IconSquare } from "@tabler/icons-react"
import { ColumnDef } from "@tanstack/react-table"
import { getFormattedDate } from "@/utils/get-formatted-date"
import dayjs from "dayjs"

export function SimpleReminderTable() {
  const { data: authenticatedUser } = useGetAuthenticatedUserSuspense()
  const [userFilter, setUserFilter] = React.useState<number | undefined>(
    authenticatedUser?.userId
  )
  const [showCompleted, setShowCompleted] = React.useState<boolean>(false)
  const { t } = useTranslation("app_server")
  const columns: ColumnDef<ReminderDto>[] = [
    {
      accessorKey: "userId",
      header: t("User"),
      cell: ({ row }) => (
        <Group gap="xs" wrap="nowrap">
          {row.original.completed ? (
            <IconCircleCheck color="var(--mantine-color-green-6)" />
          ) : (
            <IconSquare color="var(--mantine-color-red-6)" />
          )}
          <span style={{ fontSize: "var(--mantine-font-size-sm)" }}>
            <PersonNameWithUserNameFallback userId={row.original.userId} />
          </span>
        </Group>
      ),
    },
    {
      accessorKey: "time",
      header: t("DueDate"),
      cell: ({ row }) => {
        const value = row.original.time
        if (!value) return ""

        const date = dayjs(value)
        if (!date.isValid()) return ""

        return getFormattedDate(value, date.isToday() ? "LT" : "L")
      },
    },
    {
      accessorKey: "text",
      header: t("Text"),
      cell: ({ row }) => {
        return (
          <span style={{ fontSize: "var(--mantine-font-size-sm)" }}>
            {row.original.text}
          </span>
        )
      },
    },
  ]
  const { objectTypeId, objectId } = useObjectTypeAndIdFromUrl()
  const requestParams = {
    userId: userFilter,
    showCompleted,
    objectTypeId,
    objectId,
  }

  return (
    <Stack>
      <Group mb="md">
        <InfiniteUserSelect
          value={userFilter?.toString()}
          onSelectionChange={(val) =>
            setUserFilter(val ? Number(val) : undefined)
          }
          placeholder={t("SelectUser")}
          clearable
          filterOptions={{ userTypeFilter: "UsersAndGroups" }}
        />
        <Switch
          label={t("ShowCompletedReminders")}
          checked={showCompleted}
          onChange={(event) => setShowCompleted(event.currentTarget.checked)}
        />
      </Group>
      <InfiniteScrollingTable
        id="simple-reminders-table"
        infiniteQueryResult={useDefaultInfiniteQuery(
          getFilteredRemindersPaged,
          (pageRequest) => ({
            ...pageRequest,
            ...requestParams,
          }),
        )}
        columnDefinitions={columns}
        getRowProps={(row) => ({
          onClick: () => {
            const params = new URLSearchParams(window.location.search)
            params.set("reminderDetailOpened", "true")
            params.set("reminderId", String(row.original.reminderId))
            window.history.replaceState(null, "", `?${params.toString()}`)
          },
        })}
      />
    </Stack>
  )
}

